import type { ReactElement } from 'react'
import {
  CartesianGrid,
  Legend,
  Line,
  ComposedChart,
  Bar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { PontoSerie } from '../types'

import { EmptyState } from '@/components/ui/empty-state'
import { moedaCompacta, percentual } from '@/lib/formato'

const MESES_CURTOS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
]

function rotuloMes(mes: string): string {
  const [, m] = mes.split('-')
  return MESES_CURTOS[Number(m) - 1] ?? mes
}

/**
 * Formatadores extraídos do JSX: é onde mora a lógica do gráfico, e dentro do
 * recharts eles nunca rodam em teste (o container não tem dimensão em jsdom).
 */
export function formatarTick(valor: number): string {
  return moedaCompacta(valor)
}

export function formatarValor(valor: unknown, nome: unknown): [string, string] {
  return [moedaCompacta(Number(valor)), String(nome)]
}

export function formatarRotulo(rotulo: unknown, ponto?: PontoSerie): string {
  if (!ponto || ponto.meta <= 0) return String(rotulo)
  return `${String(rotulo)} · ${percentual(ponto.atingimento, 0)} da meta`
}

export function formatarRotuloDoTooltip(
  rotulo: unknown,
  itens?: readonly { payload?: unknown }[],
): string {
  return formatarRotulo(rotulo, itens?.[0]?.payload as PontoSerie | undefined)
}

export interface GraficoSerieProps {
  pontos: PontoSerie[]
}

/**
 * Bookings por mês contra a meta. Barra para o realizado e linha para a meta:
 * duas linhas sobrepostas fariam o leitor comparar inclinação, quando a
 * pergunta é "bateu ou não bateu" em cada mês.
 */
export function GraficoSerie({ pontos }: GraficoSerieProps): ReactElement {
  if (pontos.length === 0) {
    return <EmptyState size="sm" title="Sem histórico no período." />
  }

  const dados = pontos.map((p) => ({
    ...p,
    rotulo: rotuloMes(p.mes),
  }))

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={dados} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="rotulo"
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            width={70}
            tickFormatter={formatarTick}
          />
          <Tooltip
            contentStyle={{
              background: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              color: 'hsl(var(--popover-foreground))',
            }}
            formatter={formatarValor}
            labelFormatter={formatarRotuloDoTooltip}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="bookings" name="Realizado" fill="#e9c46a" radius={[4, 4, 0, 0]} />
          <Line
            dataKey="meta"
            name="Meta"
            type="monotone"
            stroke="#5fe3c0"
            strokeDasharray="4 4"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
